/**
 * IP18 Inspire — one shrinking frame; hard-cut to the next shot while
 * scale goes 1→~0.29 (Figma 17303:715), then the last shot fades out at that
 * size (not scale 0) to reveal copy. Copy lifts and fades on the pinned
 * stage while the next section covers from below.
 */
(function () {
  'use strict';

  var EXIT_VH = 100;
  var HOLD_VH = 50;
  var FADE_VH = 50;
  var SHOT_VH = 70;
  var MIN_SCALE = 565.5 / 1920;
  var RADIUS = 80;
  var COPY_LIFT = 80;

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function mapRange(p, a0, a1) {
    if (p <= a0) return 0;
    if (p >= a1) return 1;
    return (p - a0) / (a1 - a0);
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function progressInTrack(track) {
    var rect = track.getBoundingClientRect();
    var range = Math.max(1, rect.height - window.innerHeight);
    return clamp(-rect.top / range, 0, 1);
  }

  class Ip18InspirePin extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._track = this.querySelector('[data-ip18-inspire-track]');
      this._frame = this.querySelector('[data-ip18-inspire-frame]');
      this._copy = this.querySelector('[data-ip18-inspire-copy]');
      this._shots = Array.prototype.slice.call(this.querySelectorAll('[data-ip18-inspire-shot]'));
      this._reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onScroll = this._onScroll.bind(this);
      this._onReduce = this._onReduce.bind(this);

      this._reduce.addEventListener('change', this._onReduce);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      this._applyReduce();
      this._onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      if (this._reduce) this._reduce.removeEventListener('change', this._onReduce);
    }

    _applyReduce() {
      var off = this._reduce.matches || !this._track || !this._shots.length;
      this.classList.toggle('is-reduced', off);
      this.classList.toggle('is-pin', !off);
      if (off) this._reset();
    }

    _onReduce() {
      this._applyReduce();
      this._onScroll();
    }

    _reset() {
      if (this._copy) {
        this._copy.style.opacity = '';
        this._copy.style.transform = '';
      }
      if (this._frame) {
        this._frame.style.transform = '';
        this._frame.style.borderRadius = '';
        this._frame.style.visibility = '';
        this._frame.style.opacity = '';
      }
      this._shots.forEach(function (shot) {
        shot.style.opacity = '';
        shot.style.zIndex = '';
        shot.style.visibility = '';
        var hud = shot.querySelector('[data-ip18-inspire-hud]');
        if (hud) hud.style.opacity = '';
      });
    }

    _onScroll() {
      if (!this.classList.contains('is-pin')) return;

      var n = this._shots.length;
      var imgUnits = n * SHOT_VH;
      var total = imgUnits + FADE_VH + HOLD_VH + EXIT_VH;
      var p = progressInTrack(this._track);
      var imgEnd = imgUnits / total;
      var fadeEnd = (imgUnits + FADE_VH) / total;
      var holdEnd = (imgUnits + FADE_VH + HOLD_VH) / total;

      var scale = MIN_SCALE;
      var frameOp = 0;
      var idx = n > 0 ? n - 1 : 0;

      if (n > 0 && p < imgEnd) {
        var raw = p / imgEnd;
        var t = easeInOut(raw);
        scale = 1 - t * (1 - MIN_SCALE);
        frameOp = 1;
        idx = Math.min(n - 1, Math.floor(raw * n));
      } else if (n > 0 && p < fadeEnd) {
        frameOp = 1 - easeInOut(mapRange(p, imgEnd, fadeEnd));
      }

      if (this._frame) {
        this._frame.style.transform = 'scale(' + scale + ')';
        this._frame.style.borderRadius = (1 - scale) * RADIUS + 'px';
        this._frame.style.opacity = String(frameOp);
        this._frame.style.visibility = frameOp < 0.001 ? 'hidden' : 'visible';
      }

      var hudMul = mapRange(scale, 0.85, 1);

      this._shots.forEach(function (shot, j) {
        var on = j === idx;
        shot.style.opacity = on ? '1' : '0';
        shot.style.zIndex = on ? '2' : '1';
        shot.style.visibility = on ? 'visible' : 'hidden';
        var hud = shot.querySelector('[data-ip18-inspire-hud]');
        if (hud) hud.style.opacity = on ? String(hudMul) : '0';
      });

      if (!this._copy) return;

      var copyOp = 1;
      var copyY = 0;
      if (p > holdEnd) {
        var e = easeInOut(mapRange(p, holdEnd, 1));
        copyOp = 1 - e;
        copyY = -COPY_LIFT * e;
      }
      this._copy.style.opacity = String(copyOp);
      this._copy.style.transform = copyY ? 'translate3d(0,' + copyY + 'px,0)' : 'none';
    }
  }

  if (!customElements.get('ip18-inspire-pin')) {
    customElements.define('ip18-inspire-pin', Ip18InspirePin);
  }
})();
